import { DefaultCashBasisAccountCodes } from "@common/constants/domains/accounting/accounts";
import {
    SCorpAccountCodes,
    SolePropEquityAccountCodes,
} from "@common/constants/domains/accounting/chartOfAccounts";
import {
    DefaultKickAccount,
    getDefaultAccountsByCode,
} from "@common/constants/domains/accounting/defaultAccountNames";
import {
    isCorporationLedger,
    isPartnershipLedger,
    isSolePropLedger,
} from "@common/constants/domains/accounting/helpers";
import { AccountRole } from "@common/types/domains/accounting/account";
import { EntityProfileType } from "@common/types/entity";

const COMMON_ROLE_BY_KICK_ORIGINAL_CODE = new Map<number, AccountRole>([
    [
        DefaultCashBasisAccountCodes.uncategorizedIncome,
        AccountRole.uncategorizedIncome,
    ],
    [
        DefaultCashBasisAccountCodes.uncategorizedExpenses,
        AccountRole.uncategorizedExpense,
    ],
    [
        DefaultCashBasisAccountCodes.accountsReceivable,
        AccountRole.accountsReceivable,
    ],
    [DefaultCashBasisAccountCodes.accountsPayable, AccountRole.accountsPayable],
    [DefaultCashBasisAccountCodes.revenue, AccountRole.defaultRevenue],
    [DefaultCashBasisAccountCodes.badDebts, AccountRole.badDebtExpense],
    [DefaultCashBasisAccountCodes.cashOnHands, AccountRole.cashOnHand],
    [
        DefaultCashBasisAccountCodes.bankTransferClearing,
        AccountRole.bankTransferClearing,
    ],
    [
        DefaultCashBasisAccountCodes.paymentTransferClearing,
        AccountRole.paymentTransferClearing,
    ],
    [
        DefaultCashBasisAccountCodes.creditCardClearing,
        AccountRole.creditCardClearing,
    ],
    [
        DefaultCashBasisAccountCodes.salariesAndWagesClearing,
        AccountRole.payrollClearing,
    ],
    [
        DefaultCashBasisAccountCodes.otherCurrentLiabilities,
        AccountRole.reimbursementsClearing,
    ],
    [DefaultCashBasisAccountCodes.salesTaxPayable, AccountRole.salesTaxPayable],
    [
        DefaultCashBasisAccountCodes.customerDeposits,
        AccountRole.customerDeposits,
    ],
    [
        DefaultCashBasisAccountCodes.discountsAndAllowances,
        AccountRole.discountsAndAllowances,
    ],
    [DefaultCashBasisAccountCodes.prepaidExpenses, AccountRole.prepaidExpenses],
    [
        DefaultCashBasisAccountCodes.intercompanyReceivables,
        AccountRole.intercompanyReceivable,
    ],
    [
        DefaultCashBasisAccountCodes.intercompanyPayables,
        AccountRole.intercompanyPayable,
    ],
]);

export function collectOccupiedRoles(
    accounts: Array<{ role: AccountRole | null }>,
): Set<AccountRole> {
    return new Set(
        accounts.flatMap((account) =>
            account.role === null ? [] : [account.role],
        ),
    );
}

interface GetKickNativeAccountRoleParams {
    kickOriginalCode: number | null;
    entityLegalType: EntityProfileType;
}

export function getKickNativeAccountRole({
    kickOriginalCode,
    entityLegalType,
}: GetKickNativeAccountRoleParams): AccountRole | null {
    if (kickOriginalCode === null) {
        return null;
    }

    const commonRole = COMMON_ROLE_BY_KICK_ORIGINAL_CODE.get(kickOriginalCode);
    if (commonRole !== undefined) {
        return commonRole;
    }

    if (isCorporationLedger(entityLegalType)) {
        if (kickOriginalCode === SCorpAccountCodes.Contributions) {
            return AccountRole.ownerContributions;
        }
        if (kickOriginalCode === SCorpAccountCodes.Distributions) {
            return AccountRole.ownerDraws;
        }
        if (kickOriginalCode === SCorpAccountCodes.RetainedEarnings) {
            return AccountRole.retainedEarningsOrOwnersCapital;
        }
        return null;
    }

    if (
        isSolePropLedger(entityLegalType) ||
        isPartnershipLedger(entityLegalType)
    ) {
        if (kickOriginalCode === SolePropEquityAccountCodes.OwnerInvestments) {
            return AccountRole.ownerContributions;
        }
        if (kickOriginalCode === SolePropEquityAccountCodes.OwnerDraws) {
            return AccountRole.ownerDraws;
        }
        if (kickOriginalCode === SolePropEquityAccountCodes.OwnerCapital) {
            return AccountRole.retainedEarningsOrOwnersCapital;
        }
    }

    return null;
}

interface GetKickDefaultRoleHolderParams {
    role: AccountRole;
    entityLegalType: EntityProfileType;
}

export function getKickDefaultRoleHolder({
    role,
    entityLegalType,
}: GetKickDefaultRoleHolderParams): DefaultKickAccount | null {
    for (const [kickOriginalCode, account] of getDefaultAccountsByCode(
        entityLegalType,
    )) {
        if (
            getKickNativeAccountRole({ kickOriginalCode, entityLegalType }) !==
            role
        ) {
            continue;
        }
        return account;
    }
    return null;
}

export interface KickRoleHolder {
    role: AccountRole;
    defaultHolder: DefaultKickAccount;
}

export function getKickRoleHoldersForEntityLegalType(
    entityLegalType: EntityProfileType,
): KickRoleHolder[] {
    return Object.values(AccountRole).flatMap((role) => {
        const defaultHolder = getKickDefaultRoleHolder({
            role,
            entityLegalType,
        });
        return defaultHolder ? [{ role, defaultHolder }] : [];
    });
}
